import { LayersWithDeps, ResourceType, ServiceInfoDc } from "@evergis/api";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { dialogResourceSelector } from "../../../../../../ducks/dialogs/selectors";
import { getCopyJobInput } from "../../../../../../evergis/core/copy/getCopyJobInput";
import { useLayerPreview } from "../../../../../../hooks/preview/usePreview";
import { getResourceName } from "../../../../../../utils/resourceName";
import { getMetaFromResource, useResourceMeta } from "../../../../../../hooks/useResourceMeta";
import { useAfterCopy } from "../../../hooks/useAfterCopy";
import { JobSource, useCopyJobNotifications } from "../../../hooks/useCopyJobNotifications";
import { useCopyResources } from "../../../hooks/useCopyResources";
import { useUpdateMetaEffect } from "../../../hooks/useUpdateMetaEffect";
import { useLayerConditionFromTable } from "./useLayerConditionFromTable";
import { useDialogInfo } from "../../../../../../hooks/useDialogs";
import { useDefaultScaleRange } from "../../../../../../hooks/style/useDefaultScaleRange";

import { ResourceInfoDc } from "../../../../../../modules/Catalog/types";
import { CopyResourceMeta } from "../../../types";
import { LayerInfo } from "../../../../../../interfaces/resourceInfo";

export const useCopyLayerJob = (
  layerInfo: LayersWithDeps,
  copyTables: boolean,
  filter: boolean,
  showNotifications = true,
): CopyResourceMeta => {
  const currentResourceName: string = useSelector(dialogResourceSelector);
  const currentLayer: ResourceInfoDc = layerInfo && layerInfo.layers && layerInfo.layers[0];
  const layerName = currentResourceName || currentLayer?.name;
  const [previewUrl] = useLayerPreview(currentLayer && currentLayer.name);
  const [meta, updateMeta] = useResourceMeta(getMetaFromResource(currentLayer, previewUrl));
  const { copyJob, copyData: jobResult, copyLoading: jobLoading, copyError: jobError } = useCopyResources();
  const [waiting, setWaiting] = useState(false);
  const tableAlias =
    copyTables && layerInfo && layerInfo.deps && layerInfo.deps.tables[0] && layerInfo.deps.tables[0].alias;
  const notificationInfos = useCopyJobNotifications(JobSource.Layers, { alias: meta.alias, tableAlias, jobResult });
  const layerConditionFromTable = useLayerConditionFromTable(currentLayer as LayerInfo);
  const { options: dialogOptions } = useDialogInfo();
  const defaultScaleRange = useDefaultScaleRange();

  const { setInnerTasks } = useAfterCopy(
    notificationInfos,
    { jobResult, jobLoading, jobError },
    (filter ? [currentLayer] : []) as ServiceInfoDc[],
    false,
    copyTables,
    showNotifications,
  );

  useUpdateMetaEffect(currentLayer, previewUrl, updateMeta);

  const onApply = useCallback(async () => {
    if (!layerInfo || !meta) {
      return Promise.resolve("");
    }

    const { alias, description } = meta;
    const newResourceName: string = getResourceName();

    const [, tableInput, innerTasks] = getCopyJobInput({
      layers: layerInfo.layers,
      tables: layerInfo.deps.tables,
      copyTables: copyTables || Boolean(dialogOptions?.fromTable) || Boolean(dialogOptions?.fromFeatureCard),
      inputCondition: layerConditionFromTable,
      defaultScaleRange,
    });

    const copyWith: string[] =
      copyTables || dialogOptions?.fromTable || dialogOptions?.fromFeatureCard
        ? tableInput.map(item => item.copyName)
        : null;

    setWaiting(true);
    setInnerTasks(innerTasks);

    await copyJob({
      input: [
        {
          type: ResourceType.Layer,
          name: layerName,
          copyName: newResourceName,
          copyAlias: alias,
          copyDescription: description,
          copyWith,
        },
        ...tableInput,
      ],
    });

    return Promise.resolve(newResourceName);
  }, [
    layerInfo,
    meta,
    copyTables,
    dialogOptions,
    layerConditionFromTable,
    defaultScaleRange,
    setInnerTasks,
    copyJob,
    layerName,
  ]);

  return {
    meta,
    updateMeta,
    onApply,
    waiting,
  };
};
